import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import Sidebar from './components/layout/Sidebar';
import EmailListPage from './pages/EmailListPage';
import DashboardPage from './pages/DashboardPage';
import SenderRulesPage from './pages/SenderRulesPage';
import AIAssistantsPage from './pages/AIAssistantsPage';
import AIDailyReportsPage from './pages/AIDailyReportsPage';
import TestAIAnalysis from './pages/TestAIAnalysis';
import EmailDetail from './pages/EmailDetail';
import SettingsPage from './pages/SettingsPage';
import ChatRoomPage from './pages/ChatRoomPage';
import EnvIndicator from './components/EnvIndicator';
import { EmailStats } from './types';
import { logEnvInfo, envConfig } from './config/env';
import './App.css';

// 初始化环境配置日志
logEnvInfo();

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState('chat');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showComposer, setShowComposer] = useState(false);

  // 根据路由更新当前页面
  useEffect(() => {
    const path = location.pathname;
    if (path === '/emails') {
      setCurrentPage('emails');
    } else if (path.startsWith('/chat')) {
      setCurrentPage('chat');
    } else if (path === '/sender-rules') {
      setCurrentPage('sender-rules');
    } else if (path === '/ai-assistants') {
      setCurrentPage('ai-assistants');
    // } else if (path === '/ai-reports') {
    //   setCurrentPage('ai-reports');
    } else if (path === '/settings') {
      setCurrentPage('settings');
    } else {
      setCurrentPage('chat');
    }
  }, [location]);

  // 获取未读邮件数量
  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${envConfig.API_BASE_URL}/emails/stats`);
      const data: EmailStats = await response.json();
      setUnreadCount(data.unread_emails || 0);
    } catch (error) {
      console.error('获取未读邮件数量失败:', error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    // 每30秒更新一次未读数量
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handlePageChange = (page: string) => {
    // 使用React Router的navigate进行路由跳转
    switch (page) {
      case 'emails':
        navigate('/emails');
        break;
      case 'chat':
        navigate('/chat');
        break;
      case 'sender-rules':
        navigate('/sender-rules');
        break;
      case 'ai-assistants':
        navigate('/ai-assistants');
        break;
      // case 'ai-reports':
      //   navigate('/ai-reports');
      //   break;
      case 'settings':
        navigate('/settings');
        break;
      default:
        navigate('/');
        break;
    }
  };

  // 处理撰写邮件
  const handleComposeEmail = () => {
    // 如果当前不在邮件页面，先导航到邮件页面
    if (currentPage !== 'emails') {
      navigate('/emails');
    }
    setShowComposer(true);
  };

  // 关闭撰写器
  const handleComposeClose = () => {
    setShowComposer(false);
  };

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <div className="flex h-screen theme-bg-secondary">
              <Sidebar 
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onComposeEmail={handleComposeEmail}
                unreadCount={unreadCount}
              />
              
              <div className="flex-1 flex flex-col overflow-hidden">
                <Routes>
                  <Route path="/" element={<ChatRoomPage />} />
                  <Route path="/emails" element={
                    <EmailListPage 
                      showComposer={showComposer}
                      onComposeClose={handleComposeClose}
                    />
                  } />
                  <Route path="/emails/:id" element={<EmailDetail />} />
                  <Route path="/chat" element={<ChatRoomPage />} />
                  <Route path="/chat/:userEmail" element={<ChatRoomPage />} />
                  <Route path="/sender-rules" element={<SenderRulesPage />} />
                  <Route path="/ai-assistants" element={<AIAssistantsPage />} />
                  <Route path="/ai-reports" element={<AIDailyReportsPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/test-ai" element={<TestAIAnalysis />} />
                  <Route path="/settings" element={<SettingsPage />} />
                </Routes>
              </div>
            </div>
          </ProtectedRoute>
        } />
      </Routes>
      
      {/* 环境指示器 */}
      <EnvIndicator />
    </>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;